import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, LogOut, MapPin, ChevronRight } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/context/ThemeContext";
import { StatusBadge } from "@/components/common";

export function DriverShell({ children, back }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          {back || (
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Truck className="h-5 w-5" /></div>
              <div><div className="text-sm font-bold leading-tight" data-testid="driver-name">{user?.name}</div><div className="text-[11px] text-muted-foreground">{user?.company?.name}</div></div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button data-testid="driver-logout-btn" onClick={logout} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border bg-card text-muted-foreground"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-4 px-3 py-4">{children}</main>
    </div>
  );
}

export default function DriverHome() {
  const [trips, setTrips] = useState(null);
  const [tab, setTab] = useState("active");
  useEffect(() => { api.get("/bookings").then((r) => setTrips(r.data)); }, []);
  const list = (trips || []).filter((t) => (tab === "active" ? t.status !== "Delivered" : t.status === "Delivered"));
  return (
    <DriverShell>
      <h1 className="px-1 text-3xl font-bold tracking-tight">My Trips</h1>
      <div className="grid grid-cols-2 gap-1 rounded-xl border bg-card p-1">
        {[["active", "Active"], ["done", "Completed"]].map(([k, l]) => (
          <button key={k} data-testid={`driver-tab-${k}`} onClick={() => setTab(k)} className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {trips && list.length === 0 && <div className="card-base p-8 text-center text-sm text-muted-foreground" data-testid="driver-no-trips">No {tab === "active" ? "active" : "completed"} trips.</div>}
      {list.map((t, i) => (
        <Link key={t.id} to={`/driver/trip/${t.id}`} data-testid={`driver-trip-${t.lr_number}`} className="card-base block p-4 fade-up active:scale-[0.99] transition-transform" style={{ animationDelay: `${i * 50}ms` }}>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs font-semibold text-muted-foreground">{t.lr_number} · {fmtDate(t.booking_date)}</span>
            <StatusBadge status={t.status} />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <MapPin className="h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-lg font-bold leading-tight">{t.pickup_location}</div>
              <div className="text-lg font-bold leading-tight text-muted-foreground">→ {t.drop_location}</div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="mt-3 flex gap-4 text-xs text-muted-foreground"><span className="font-mono font-semibold text-foreground">{t.vehicle_number}</span><span>{t.material_type} · {t.weight_tons} T</span></div>
        </Link>
      ))}
    </DriverShell>
  );
}
